import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Edit2, Trash2, Check, X, Plus, Minus, Info } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface EstimateLineItem {
  id?: string;
  product_id: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  discount_percent?: number;
  discount_amount?: number;
  line_total: number;
  calculation_source?: string;
  calculation_note?: string;
}

interface EditableLineItemRowProps {
  item: EstimateLineItem;
  onUpdate: (id: string, updates: Partial<EstimateLineItem>) => void;
  onDelete: (id: string) => void;
}

export function EditableLineItemRow({ item, onUpdate, onDelete }: EditableLineItemRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedQuantity, setEditedQuantity] = useState(item.quantity);
  const [editedPrice, setEditedPrice] = useState(item.unit_price);

  const handleSave = () => {
    // Validate inputs
    if (editedQuantity <= 0) {
      toast({
        title: "Invalid quantity",
        description: "Quantity must be greater than 0",
        variant: "destructive",
      });
      return;
    }
    
    if (editedPrice <= 0) {
      toast({
        title: "Invalid price",
        description: "Price must be greater than 0",
        variant: "destructive",
      });
      return;
    }

    const newLineTotal = editedQuantity * editedPrice;
    onUpdate(item.id!, {
      quantity: editedQuantity,
      unit_price: editedPrice,
      line_total: newLineTotal,
      calculation_source: 'edited',
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedQuantity(item.quantity);
    setEditedPrice(item.unit_price);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="pb-3 border-b space-y-3 w-full">
        {/* Product Description */}
        <p className="text-base font-medium break-words">{item.description}</p>
        
        {/* Quantity Controls - Stack vertically on iPad */}
        <div className="flex flex-col gap-3">
          <div className="w-full">
            <Label className="text-sm text-muted-foreground mb-1.5 block">Quantity</Label>
            <div className="flex items-center gap-2 w-full">
              <Button 
                size="icon" 
                variant="outline" 
                className="h-11 w-11 shrink-0"
                onClick={() => setEditedQuantity(Math.max(0, editedQuantity - 1))}
              >
                <Minus className="w-4 h-4" />
              </Button>
              <Input
                type="number"
                value={editedQuantity}
                onChange={(e) => setEditedQuantity(parseFloat(e.target.value) || 0)}
                className="h-11 text-center min-w-0 flex-1 text-base"
                min="0"
                step="0.01"
              />
              <span className="text-sm text-muted-foreground px-1 shrink-0 whitespace-nowrap">
                {item.unit}
              </span>
              <Button 
                size="icon" 
                variant="outline" 
                className="h-11 w-11 shrink-0"
                onClick={() => setEditedQuantity(editedQuantity + 1)}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Price Input - Full width on iPad */}
          <div className="w-full">
            <Label className="text-sm text-muted-foreground mb-1.5 block">Unit Price</Label>
            <div className="flex items-center gap-2 w-full">
              <span className="text-base text-muted-foreground shrink-0">$</span>
              <Input
                type="number"
                value={editedPrice}
                onChange={(e) => setEditedPrice(parseFloat(e.target.value) || 0)}
                className="h-11 min-w-0 flex-1 text-base"
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {/* Total and Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2 border-t">
          <div>
            <p className="text-sm text-muted-foreground">Line Total</p>
            <p className="text-xl font-bold text-primary">
              ${(editedQuantity * editedPrice).toFixed(2)}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button 
              size="default" 
              variant="outline"
              onClick={handleCancel}
              className="w-full sm:w-auto h-11"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button 
              size="default" 
              onClick={handleSave}
              className="w-full sm:w-auto h-11"
            >
              <Check className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-3 border-b last:border-0 w-full">
      <div className="flex-1 min-w-0 max-w-full">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <p className="text-base font-medium break-words">
            {item.description}
          </p>
          {item.calculation_source && (
            <Badge 
              variant={item.calculation_source === 'auto' ? 'secondary' : 'outline'}
              className="text-xs"
            >
              {item.calculation_source === 'auto' ? 'Auto' : 'Edited'}
            </Badge>
          )}
          {item.calculation_note && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{item.calculation_note}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <p className="text-sm text-muted-foreground break-words">
          {item.quantity} {item.unit} × ${item.unit_price.toFixed(2)}
        </p>
      </div>
      <div className="flex items-center justify-between gap-3 w-full">
        <p className="text-lg md:text-xl font-bold text-primary">
          ${item.line_total.toFixed(2)}
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-11 w-11"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Edit quantity or price</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {item.id && (
            <Button
              size="icon"
              variant="ghost"
              className="h-11 w-11 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => onDelete(item.id!)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
